<script lang="ts">
  // externals
  import { createEventDispatcher } from "svelte";

  // components
  import { convertToLocalWithTime } from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";

  // stores
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";

  // types
  import { DecisionModalType } from "@retailer/svelte/LimitedOrderingProductRequests/DecisionModal.svelte";
  import type { LimitedOrderingProductRequest } from "@shared/types/LimitedOrderingProductRequest";
  import Icon from "@shared/svelte/Components/Icon.svelte";

  import { EventTracking } from "@shared/services/event_tracking/events";
  import { ProductApprovalsBulkActionSelectSingleEntity } from "@shared/services/event_tracking/entities/products/product_approvals_bulk_actions_select_single_entity";

  import ProductApprovalStore from "@shared/svelte/Stores/ProductApprovalStore";

  export let selectedProducts: LimitedOrderingProductRequest[] = [];
  export let hasBulkApprovalRollout: boolean;

  const dispatch = createEventDispatcher();
  const { trackClick } = EventTracking;

  const openDecisionModal = (
    productRequest: LimitedOrderingProductRequest,
    modalAction: string,
  ) => {
    const data = { modalAction, productRequest };
    dispatch("openDecisionModal", data);
  };

  const openRequestNoteModal = (productRequest: LimitedOrderingProductRequest) =>
    dispatch("openRequestNoteModal", productRequest);

  const toggleProductCheckbox = productRequestId => {
    trackClick(
      "product_approvals_bulk_actions_select_single",
      new ProductApprovalsBulkActionSelectSingleEntity({
        limitedOrderingProductRequestId: productRequestId,
      }),
    );
    dispatch("toggleProductCheckbox", productRequestId);
  };
</script>

<style>
  tr > td:first-child {
    padding-left: 32px;
    padding-top: 24px !important;
    padding-bottom: 24px;
  }

  @media (max-width: 767px) {
    tr > td {
      padding: 1rem 2rem;
    }
  }

  @media (max-width: 375px) {
    tr > td {
      padding: 4px 32px;
    }
  }

  @media (min-width: 768px) {
    .hide-on-tablet {
      display: none;
    }
  }

  @media (max-width: 767px) {
    td.hide-on-mobile {
      display: none !important;
    }
  }

  tr > td:last-child {
    padding-right: 32px;
  }
  tr > td.request-note {
    padding: 1rem 0;
  }

  .fizz-checkbox.hide-on-tablet {
    min-width: 1rem;
  }

  p.product-description {
    word-wrap: break-word;
    width: 80%;
  }
</style>

<tbody>
  {#each $ProductApprovalStore.productRequests as productRequest (productRequest.id)}
    <tr id="limited-ordering-product-{productRequest.id}">
      {#if hasBulkApprovalRollout}
        <td class="fizz-row-select hide-on-mobile">
          <input
            type="checkbox"
            name="checkbox"
            data-testid={`product-checkbox-${productRequest.id}`}
            id="product-checkbox"
            class="fizz-checkbox"
            on:change={() => toggleProductCheckbox(productRequest.id)}
            bind:group={selectedProducts}
            value={productRequest.id}
          />
        </td>
      {/if}
      <td>
        <div class="fizz-flex-row-4">
          {#if hasBulkApprovalRollout}
            <input
              type="checkbox"
              name="checkbox"
              data-testid={`mobile-product-checkbox-${productRequest.id}`}
              id="product-checkbox"
              class="fizz-checkbox hide-on-tablet"
              on:change={() => toggleProductCheckbox(productRequest.id)}
              bind:group={selectedProducts}
              value={productRequest.id}
            />
          {/if}
          <ProductImage
            product={productRequest.product.product_line}
            height="100"
            width="75"
            fizz={true}
            className="fizz-stack-4 fizz-inline-8"
          />
          <div>
            <p class="fizz-text-strong product-description">
              <UIRouterLink
                stateName={"layout.product_detail"}
                params={{ productId: productRequest.product.product_line.id }}
              >
                {productRequest.product.product_line.name}
              </UIRouterLink>
            </p>
            <p class="fizz-subdued">Size: {productRequest.product.description}</p>
          </div>
        </div>
      </td>
      <td class="vertical-align-middle">
        <div class="fizz-flex-row-4">
          <p>
            {productRequest.retailer.name}
          </p>
        </div>
      </td>
      <td class="vertical-align-middle">
        <div class="fizz-flex-row-4">
          <p>
            {productRequest.retailer.state}
          </p>
        </div>
      </td>
      <td class="vertical-align-middle">
        {productRequest.requested_by_user.full_name}
      </td>
      <td class="vertical-align-middle fizz-nowrap">
        {convertToLocalWithTime(productRequest.created_at)}
      </td>
      <td class="vertical-align-middle" class:request-note={$LayoutStore.isDesktop}>
        {#if productRequest.request_note !== null}
          <button
            class="fizz-icon-button-small"
            aria-label="request-note"
            on:click={() => openRequestNoteModal(productRequest)}
          >
            <Icon type="Document" width="24" height="24" fill="#848484" />
          </button>
        {/if}
      </td>
      <td
        class="fizz-align-stretch vertical-align-middle"
        class:fizz-flex-column={$LayoutStore.isDesktop}
        class:fizz-flex-row-8={$LayoutStore.isMobile}
      >
        <button
          class="fizz-button-secondary fizz-full-width fizz-stack-8"
          on:click={() => openDecisionModal(productRequest, DecisionModalType.approve)}
        >
          Approve request
        </button>
        <button
          class="fizz-button fizz-full-width"
          on:click={() => openDecisionModal(productRequest, DecisionModalType.decline)}
        >
          Decline request
        </button>
      </td>
    </tr>
  {/each}
</tbody>
